import { useCallback, useEffect, useState } from 'react';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { BaseRates } from '../BaseRates/baseRates';
import { describeError } from './scopingModels';

export interface LoadClient {
  httpGet: (path: string) => Promise<unknown>;
}

export interface Loaded<T> {
  value: T | null;
  missing: boolean;
  errors: string[];
  reload: () => void;
}

/**
 * Loads `path`, again whenever it changes or on reload; a 404 is reported as missing and any other
 * failure as the messages `describe` gives. `describe` must keep its identity across renders, e.g.
 * a module-level function, or each render loads again.
 */
export const useLoaded = <T extends unknown>(
  apiClient: LoadClient,
  path: string | null,
  describe: (error: ApiError) => string[] = describeError
): Loaded<T> => {
  const [value, setValue] = useState<T | null>(null);
  const [missing, setMissing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setValue(null);
    setMissing(false);
    setErrors([]);
    if (path === null) {
      return undefined;
    }
    let active = true;
    apiClient
      .httpGet(path)
      .then(response => {
        if (active) {
          setValue(response as T);
        }
      })
      .catch((error: ApiError) => {
        if (!active) {
          return;
        }
        if (error.status === 404) {
          setMissing(true);
        } else {
          setErrors(describe(error));
        }
      });
    return (): void => {
      active = false;
    };
  }, [apiClient, path, describe, attempt]);

  const reload = useCallback((): void => setAttempt(current => current + 1), []);

  return { value, missing, errors, reload };
};

/**
 * The organization's live base rates, which every scoping model is generated from; `missing`
 * when it has none. Loads nothing while `active` is false.
 */
export const useBaseRate = (apiClient: LoadClient, active = true): Loaded<BaseRates> =>
  useLoaded<BaseRates>(apiClient, active ? '/baseRate' : null);
